import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type CookieConsentValue = 'essential' | 'analytics' | 'marketing';

type CookieConsentRecord = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: string;
};

type ConsentPreferences = Pick<CookieConsentRecord, 'analytics' | 'marketing'>;

type CookieConsentCategoryDisplay = {
  label: string;
  description: string;
};

export type CookieConsentDisplay = {
  title: string;
  description: string;
  acceptAll: string;
  rejectNonEssential: string;
  cookieSettings: string;
  summaryAny: string;
  summaryEssential: string;
  learnMore: string;
  essentialActive: string;
  detailsHeading: string;
  detailsCopy: string;
  savePreferences: string;
  back: string;
  closeAriaLabel: string;
  categories: Record<CookieConsentValue, CookieConsentCategoryDisplay>;
};

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_CONSENT_VERSION = '1';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

const createConsentRecord = (overrides: Partial<CookieConsentRecord> = {}): CookieConsentRecord => ({
  essential: true,
  analytics: false,
  marketing: false,
  timestamp: new Date().toISOString(),
  version: COOKIE_CONSENT_VERSION,
  ...overrides,
});

const getStoredConsent = (): CookieConsentRecord | null => {
  if (typeof window === 'undefined') return null;

  const localData = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  if (localData) {
    try {
      const parsed = JSON.parse(localData) as CookieConsentRecord;
      if (parsed?.version === COOKIE_CONSENT_VERSION && parsed?.essential === true) {
        return parsed;
      }
    } catch {
      // malformed localStorage value
    }
  }

  const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_CONSENT_KEY + '=(?<value>[^;]+)'));
  if (match?.groups?.value) {
    try {
      const parsed = JSON.parse(decodeURIComponent(match.groups.value)) as CookieConsentRecord;
      if (parsed?.version === COOKIE_CONSENT_VERSION && parsed?.essential === true) {
        return parsed;
      }
    } catch {
      // malformed cookie value
    }
  }

  return null;
};

const COOKIE_CONSENT_RESOLVED_EVENT = 'cookieConsentResolved';

const dispatchConsentEvent = (consent: CookieConsentRecord | null) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<CookieConsentRecord | null>(COOKIE_CONSENT_RESOLVED_EVENT, { detail: consent }));
};

const saveConsentRecord = (consent: CookieConsentRecord) => {
  if (typeof window === 'undefined') return;

  const serialized = JSON.stringify(consent);
  window.localStorage.setItem(COOKIE_CONSENT_KEY, serialized);

  document.cookie = `${COOKIE_CONSENT_KEY}=${encodeURIComponent(serialized)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax; Secure`;
  dispatchConsentEvent(consent);
};

export const waitForConsentResolved = (): Promise<CookieConsentRecord | null> => {
  if (typeof window === 'undefined') {
    return Promise.resolve(getStoredConsent());
  }

  const current = getStoredConsent();
  if (current !== null) {
    return Promise.resolve(current);
  }

  return new Promise((resolve) => {
    const onResolved = (event: Event) => {
      window.removeEventListener(COOKIE_CONSENT_RESOLVED_EVENT, onResolved);
      resolve((event as CustomEvent<CookieConsentRecord | null>).detail);
    };

    window.addEventListener(COOKIE_CONSENT_RESOLVED_EVENT, onResolved);
  });
};

interface CookieConsentContextValue {
  consent: CookieConsentRecord | null;
  isConsentGiven: boolean;
  isResolved: boolean;
  isBannerVisible: boolean;
  display: CookieConsentDisplay;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  savePreferences: (preferences: ConsentPreferences) => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

const createDisplay = (t: (key: string) => string): CookieConsentDisplay => ({
  title: t('cookieConsent.title'),
  description: t('cookieConsent.description'),
  acceptAll: t('cookieConsent.acceptAll'),
  rejectNonEssential: t('cookieConsent.rejectNonEssential'),
  cookieSettings: t('cookieConsent.cookieSettings'),
  summaryAny: t('cookieConsent.summaryAny'),
  summaryEssential: t('cookieConsent.summaryEssential'),
  learnMore: t('cookieConsent.learnMore'),
  essentialActive: t('cookieConsent.essentialActive'),
  detailsHeading: t('cookieConsent.detailsHeading'),
  detailsCopy: t('cookieConsent.detailsCopy'),
  savePreferences: t('cookieConsent.savePreferences'),
  back: t('cookieConsent.back'),
  closeAriaLabel: t('cookieConsent.closeAriaLabel'),
  categories: {
    essential: {
      label: t('cookieConsent.categories.essential'),
      description: t('cookieConsent.categories.essentialDescription'),
    },
    analytics: {
      label: t('cookieConsent.categories.analytics'),
      description: t('cookieConsent.categories.analyticsDescription'),
    },
    marketing: {
      label: t('cookieConsent.categories.marketing'),
      description: t('cookieConsent.categories.marketingDescription'),
    },
  },
});

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [consent, setConsent] = useState<CookieConsentRecord | null>(null);
  const [isResolved, setIsResolved] = useState(false);

  const display = useMemo(() => createDisplay(t), [t]);

  useEffect(() => {
    const storedConsent = getStoredConsent();
    if (storedConsent) {
      setConsent(storedConsent);
    }
    setIsResolved(true);
    dispatchConsentEvent(storedConsent);
  }, []);

  const acceptAll = () => {
    const consentRecord = createConsentRecord({ analytics: true, marketing: true });
    saveConsentRecord(consentRecord);
    setConsent(consentRecord);
  };

  const rejectNonEssential = () => {
    const consentRecord = createConsentRecord({ analytics: false, marketing: false });
    saveConsentRecord(consentRecord);
    setConsent(consentRecord);
  };

  const savePreferences = (preferences: ConsentPreferences) => {
    const consentRecord = createConsentRecord({ analytics: preferences.analytics, marketing: preferences.marketing });
    saveConsentRecord(consentRecord);
    setConsent(consentRecord);
  };

  const value = useMemo(
    () => ({
      consent,
      isConsentGiven: consent !== null,
      isResolved,
      isBannerVisible: isResolved && consent === null,
      display,
      acceptAll,
      rejectNonEssential,
      savePreferences,
    }),
    [consent, isResolved, display],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = (): CookieConsentContextValue => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

export const useCookieConsentDisplay = (): CookieConsentDisplay => {
  const { display } = useCookieConsent();
  return display;
};
